<script setup lang="ts">
    // @ts-nocheck
    import { computed, onMounted, onBeforeMount, ref, toRefs } from "vue";
    import { getConfig, getErrorMessage, klinikenApi } from "@/api";
    import { flashMessage, openDialog } from "@/utils";
    import { HanteraVardplatsOption, Slutenvardsbesok } from "@/tabs/Vardplatsbokning/types";
    import DialogAktivPatient from "@/tabs/Operation/DialogAktivPatient.vue";
    import KForm from "@/components/forms/Form/KForm.vue";
    import DialogVardplatsbestallningInfo from "@/tabs/Vardplatsbokning/DialogVardplatsbestallningInfo.vue";
    import moment from "moment";
    import { useStore } from "@/store";
    import KCheckbox from "@/components/forms/inputs/Checkbox/KCheckbox.vue";

    const store = useStore();

    const props = defineProps<{
        slutenvardsbesokId: string;
        option?: HanteraVardplatsOption;
    }>();

    const { slutenvardsbesokId, option } = toRefs(props);

    const formRef = ref();
    const skrivInRef = ref();
    const skrivUtRef = ref();
    const vardplatser = ref([]);
    const anhorigplats = ref(false);

    const slutenvardsbesok = computed(() =>
        store.getters["slutenvardsbesok/slutenvardsbesokById"]({ id: slutenvardsbesokId?.value })
    );

    const converters = [
        {
            fieldName: "inskrivningstid",
            converter: ({ value }) => {
                if (!value) {
                    return;
                }
                return value;
            },
        },
        {
            fieldName: "utskrivningstid",
            converter: ({ value }) => {
                if (!value) {
                    return;
                }
                return value;
            },
        },
    ];

    const submit = async (formData: Partial<Slutenvardsbesok>) => {
        // TODO Sätter Formulate type timepicker till null. Behövs inte vid byte till K-komponent
        Object.keys(formData).forEach((key) => {
            if (!formData[key]) {
                formData[key] = null;
            }
        });

        try {
            await store.dispatch("slutenvardsbesok/partialUpdateSlutenvardsbesok", {
                slutenvardsbesok: {
                    ...formData,
                    id: slutenvardsbesokId.value,
                },
            });

            flashMessage("Vårdplatsbeställningen uppdaterad");

            emit("close");
        } catch (e) {
            openDialog("Det gick inte att spara. " + getErrorMessage(e), "error");
        }
    };

    const emit = defineEmits(["close"]);

    const cancel = () => {
        emit("close");
    };

    const loadAndMapVardplatser = async () => {
        try {
            const response = await klinikenApi.get("/slutenvard/sangar/", getConfig());
            vardplatser.value = response.data.results.map((item: any) => {
                return {
                    value: item.id,
                    label: item.namn,
                };
            });
        } catch (e) {
            openDialog(getErrorMessage(e), "error");
        }
    };

    const fetchFormData = async () => {
        const data = store.getters["slutenvardsbesok/slutenvardsbesokById"]({ id: slutenvardsbesokId?.value });

        anhorigplats.value = !!data.anhorigbesok?.aktiv;

        if (data) {
            return {
                indatum: data.indatum,
                intid: data.intid?.substring(0, 5), // TODO: Lös centralt i InputTimePicker
                utdatum: data.utdatum,
                uttid: data.uttid?.substring(0, 5), // TODO: Lös centralt i InputTimePicker
                sang: data.sang ? `${data.sang.id}` : null,
                // Vi hanterar inskriven och utskriven som tidsfält i FE, och konverterar dem till datetimes i backend
                inskrivningstid: data.inskriven_tidpunkt ? moment(data.inskriven_tidpunkt).format("HH:mm") : null,
                utskrivningstid: data.utskriven_tidpunkt ? moment(data.utskriven_tidpunkt).format("HH:mm") : null,
                anhorigplats: anhorigplats.value,
                anhorig_namn: anhorigplats.value ? data.anhorigbesok.namn : null,
                anhorig_sang: anhorigplats.value && data.anhorigbesok.sang ? `${data.anhorigbesok.sang.id}` : null,
            };
        }

        return null;
    };

    const getFormTriggers = () => {
        return [
            {
                fieldName: ["anhorig_namn", "anhorig_sang"],
                dependentOn: "anhorigplats",
                action: ({ value, dependentOnValue }: { value: unknown; dependentOnValue: unknown }) => {
                    anhorigplats.value = dependentOnValue;
                    if (dependentOnValue) {
                        return [
                            {
                                fieldName: "anhorig_namn",
                                disabled: false,
                                required: true,
                                value: value["anhorig_namn"],
                            },
                            {
                                fieldName: "anhorig_sang",
                                disabled: false,
                                required: false,
                                value: value["anhorig_sang"],
                            },
                        ];
                    } else {
                        return [
                            {
                                fieldName: "anhorig_namn",
                                disabled: true,
                                required: false,
                                value: null,
                            },
                            {
                                fieldName: "anhorig_sang",
                                disabled: true,
                                required: false,
                                value: null,
                            },
                        ];
                    }
                },
            },
        ];
    };

    onBeforeMount(async () => {
        await Promise.allSettled([
            loadAndMapVardplatser(),
            store.dispatch("slutenvardsbesok/getSlutenvardsbesokById", { id: slutenvardsbesokId.value }),
        ]);
    });

    onMounted(() => {
        switch (option?.value) {
            case HanteraVardplatsOption.SKRIV_IN:
                skrivInRef.value.$el.querySelector("input").focus();
                break;
            case HanteraVardplatsOption.SKRIV_UT:
                skrivUtRef.value.$el.querySelector("input").focus();
                break;
            default:
                break;
        }
    });
</script>

<template>
    <div class="hantera-vardplats">
        <DialogAktivPatient :patient="slutenvardsbesok.patient" />
        <DialogVardplatsbestallningInfo :vardplatsbestallning="slutenvardsbesok.vardplatsbestallning" />
        <KForm
            ref="formRef"
            id="hantera-vardplats-form"
            class="hantera-vardplats-form"
            @submit="submit"
            @cancel="cancel"
            submit-content="Spara ändringar"
            cancel-content="Avbryt"
            :initial-values-getter="fetchFormData"
            :triggers="getFormTriggers()"
            :converters="converters"
        >
            <template #default>
                <div class="form-content">
                    <FormulateInput type="datepicker" name="indatum" label="Indatum *" validation="required" />
                    <FormulateInput type="timepicker" name="intid" label="Intid *" validation="required" />
                    <FormulateInput type="datepicker" name="utdatum" label="Utdatum *" validation="required" />
                    <FormulateInput type="timepicker" name="uttid" label="Uttid *" validation="required" />
                    <FormulateInput
                        type="vueselect"
                        name="sang"
                        label="Vårdplats"
                        :options="vardplatser"
                        class="span-two"
                    />
                    <FormulateInput
                        type="timepicker"
                        ref="skrivInRef"
                        useNow
                        name="inskrivningstid"
                        label="Inskrivningstid"
                        :useDropdown="false"
                    />
                    <FormulateInput
                        type="timepicker"
                        ref="skrivUtRef"
                        useNow
                        name="utskrivningstid"
                        label="Utskrivningstid"
                        :useDropdown="false"
                    />
                    <KCheckbox name="anhorigplats" label="Anhörigplats" class="span-all" />
                    <KText
                        name="anhorig_namn"
                        label="Anhörig namn"
                        type="text"
                        :disabled="!anhorigplats"
                        :required="anhorigplats"
                    />
                    <FormulateInput
                        type="vueselect"
                        name="anhorig_sang"
                        label="Anhörigplats"
                        :options="vardplatser"
                        :disabled="!anhorigplats"
                    />
                </div>
            </template>
        </KForm>
    </div>
</template>
